import type {
    FinancialMetricSparklinePoint,
    FinancialOverviewData,
    FinancialOverviewTickerDataPoint,
} from '../../types';

const finiteNumber = (value: unknown) => {
    const number = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;

    return Number.isFinite(number) ? number : undefined;
};

const valuesFromPoints = (points: FinancialOverviewTickerDataPoint[], key: string) =>
    points.flatMap((point) => {
        const value = finiteNumber(point[key]);
        return value === undefined ? [] : [{ ...(point.date ? { date: point.date } : {}), value }];
    });

export const resolveFinancialMetricSparklineFlatDomain = (
    points: FinancialMetricSparklinePoint[],
): [number, number] | undefined => {
    const values = points.map(({ value }) => value).filter(Number.isFinite);

    if (!values.length) {
        return undefined;
    }

    const minimum = Math.min(...values);
    const maximum = Math.max(...values);

    if (minimum !== maximum) {
        return undefined;
    }

    const padding = Math.max(Math.abs(minimum) * 0.05, 1);

    return [minimum - padding, maximum + padding];
};

export const resolveFinancialMetricSparkline = (
    metricKey: string,
    points: FinancialOverviewTickerDataPoint[],
    allData: FinancialOverviewData,
): FinancialMetricSparklinePoint[] => {
    const directValues = valuesFromPoints(points, metricKey);

    if (directValues.length >= 2) {
        return directValues;
    }

    const prices = points.flatMap((point) => {
        const price = finiteNumber(point.close);
        return price === undefined ? [] : [{ ...(point.date ? { date: point.date } : {}), value: price }];
    });

    if (metricKey === 'stockPrice') {
        return prices;
    }

    const sharesOutstanding = finiteNumber(allData.enterpriseValues?.numberOfShares);

    if ((metricKey === 'marketCap' || metricKey === 'enterpriseValue') && sharesOutstanding !== undefined) {
        const marketCaps = prices.map((point) => ({ ...point, value: point.value * sharesOutstanding }));

        if (metricKey === 'marketCap') {
            return marketCaps;
        }

        const currentMarketCap = finiteNumber(allData.keyMetrics?.marketCap);
        const currentEnterpriseValue = finiteNumber(allData.keyMetrics?.enterpriseValue);

        if (currentMarketCap !== undefined && currentEnterpriseValue !== undefined) {
            const nonEquityValue = currentEnterpriseValue - currentMarketCap;
            return marketCaps.map((point) => ({ ...point, value: point.value + nonEquityValue }));
        }
    }

    return [];
};
